// TrendSummary.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getHaryanaDemandMetrics,
  getHaryanaForecastMetricsTwoDays,
} from "../../../Rest_api/RestApi";
import { H_CHART_TYPES } from "../const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { getTimeRange } from "../../Dashboard/Exchange3/Chart";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip, { MetricChipHead } from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { TrendData } from "../../../models/metrics";

// ─── Constants ────────────────────────────────────────────────────────────────

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

// ─── Props ────────────────────────────────────────────────────────────────────

export interface HaryanaSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  /** selectedSuffix is the array of active suffix objects from the parent */
  selectedSuffix: Array<{
    suffix: string;
    displayName: string;
    stackSuffix: string;
  }>;
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: Array<{
    suffix: string;
    displayName: string;
    stackSuffix: string;
  }>;
  metrics: TrendData | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function HaryanaSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix,
  onTypeChange,
  chartSuffixes,
  metrics,
}: HaryanaSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchSummaryData = useCallback(async () => {
    const trendDates = DefaultDates[activeTab]?.defaultTrendDates;
    if (!trendDates || trendDates.length === 0) {
      setSummaryData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      // Deduplicate by "startDate,endDate"
      const seenRanges = new Set<string>();
      const uniqueTrendDates = trendDates.filter((date: any) => {
        const [start, end] = date.value.split(",");
        const rangeKey = `${start},${end}`;
        if (seenRanges.has(rangeKey)) return false;
        seenRanges.add(rangeKey);
        return true;
      });

      const promises = uniqueTrendDates.map(async (date: any) => {
        const [startDate, endDate] = date.value.split(",");

        const [raw, forecastRaw] = await Promise.all([
          getHaryanaDemandMetrics(startDate, endDate),
          getHaryanaForecastMetricsTwoDays(startDate, endDate),
        ]);

        const forecastD1BySlot = new Map<number, any>();
        const forecastD2BySlot = new Map<number, any>();
        (Array.isArray(forecastRaw) ? forecastRaw : []).forEach((f: any) => {
          if (f.forecast_type === "T+1") forecastD1BySlot.set(f.slot, f);
          else if (f.forecast_type === "T+2") forecastD2BySlot.set(f.slot, f);
        });

        const actualBySlot = new Map<number, any>(
          (Array.isArray(raw) ? raw : []).map((item: any) => [item.slot, item]),
        );

        const allSlots = new Set<number>([
          ...actualBySlot.keys(),
          ...forecastD1BySlot.keys(),
          ...forecastD2BySlot.keys(),
        ]);

        return Array.from(allSlots).map((slot) => {
          const actual = actualBySlot.get(slot) ?? {};
          const fcD1 = forecastD1BySlot.get(slot) ?? {};
          const fcD2 = forecastD2BySlot.get(slot) ?? {};

          const time_string =
            actual.time_string ?? fcD1.time_string ?? fcD2.time_string;

          return {
            slot,
            time_string,
            // Actual demand
            min_demand: parseFloat(actual.min_demand?.toString() ?? "0"),
            max_demand: parseFloat(actual.max_demand?.toString() ?? "0"),
            avg_demand: parseFloat(actual.avg_demand?.toString() ?? "0"),
            mode_demand: parseFloat(actual.mode_demand?.toString() ?? "0"),
            date_of_min: actual.date_of_min,
            date_of_max: actual.date_of_max,

            // D+1 forecast fields
            forecast_min_demand_d1: parseFloat(
              fcD1.min_demand?.toString() ?? "0",
            ),
            forecast_max_demand_d1: parseFloat(
              fcD1.max_demand?.toString() ?? "0",
            ),
            forecast_avg_demand_d1: parseFloat(
              fcD1.avg_demand?.toString() ?? "0",
            ),
            forecast_mode_demand_d1: parseFloat(
              fcD1.mode_demand?.toString() ?? "0",
            ),
            forecast_min_demand_date_d1: fcD1.date_of_min,
            forecast_max_demand_date_d1: fcD1.date_of_max,

            // D+2 forecast fields
            forecast_min_demand_d2: parseFloat(
              fcD2.min_demand?.toString() ?? "0",
            ),
            forecast_max_demand_d2: parseFloat(
              fcD2.max_demand?.toString() ?? "0",
            ),
            forecast_avg_demand_d2: parseFloat(
              fcD2.avg_demand?.toString() ?? "0",
            ),
            forecast_mode_demand_d2: parseFloat(
              fcD2.mode_demand?.toString() ?? "0",
            ),
            forecast_min_demand_date_d2: fcD2.date_of_min,
            forecast_max_demand_date_d2: fcD2.date_of_max,
          };
        });
      });

      const results = await Promise.all(promises);
      const allRecords = results.flat();

      const bySlot = new Map<number, any[]>();
      allRecords.forEach((record) => {
        const group = bySlot.get(record.slot) ?? [];
        group.push(record);
        bySlot.set(record.slot, group);
      });

      const aggregated = Array.from(bySlot.values()).map((records) => {
        const n = records.length;
        const avg = (key: string) =>
          records.reduce((sum, r) => sum + (r[key] ?? 0), 0) / n;

        const minRecord = records.reduce((best, r) =>
          (r.min_demand ?? 0) < (best.min_demand ?? 0) ? r : best,
        );
        const maxRecord = records.reduce((best, r) =>
          (r.max_demand ?? 0) > (best.max_demand ?? 0) ? r : best,
        );

        return {
          slot: records[0].slot,
          time_string: records[0].time_string,
          min_demand: minRecord.min_demand,
          date_of_min: minRecord.date_of_min,
          max_demand: maxRecord.max_demand,
          date_of_max: maxRecord.date_of_max,
          avg_demand: avg("avg_demand"),
          mode_demand: avg("mode_demand"),

          ...((): any => {
            const fD1Min = records.reduce((best, r) =>
              (r.forecast_min_demand_d1 ?? 0) <
              (best.forecast_min_demand_d1 ?? 0)
                ? r
                : best,
            );
            const fD1Max = records.reduce((best, r) =>
              (r.forecast_max_demand_d1 ?? 0) >
              (best.forecast_max_demand_d1 ?? 0)
                ? r
                : best,
            );
            return {
              forecast_min_demand_d1: fD1Min.forecast_min_demand_d1,
              forecast_min_demand_date_d1: fD1Min.forecast_min_demand_date_d1,
              forecast_max_demand_d1: fD1Max.forecast_max_demand_d1,
              forecast_max_demand_date_d1: fD1Max.forecast_max_demand_date_d1,
              forecast_avg_demand_d1: avg("forecast_avg_demand_d1"),
              forecast_mode_demand_d1: avg("forecast_mode_demand_d1"),
            };
          })(),

          ...((): any => {
            const fD2Min = records.reduce((best, r) =>
              (r.forecast_min_demand_d2 ?? 0) <
              (best.forecast_min_demand_d2 ?? 0)
                ? r
                : best,
            );
            const fD2Max = records.reduce((best, r) =>
              (r.forecast_max_demand_d2 ?? 0) >
              (best.forecast_max_demand_d2 ?? 0)
                ? r
                : best,
            );
            return {
              forecast_min_demand_d2: fD2Min.forecast_min_demand_d2,
              forecast_min_demand_date_d2: fD2Min.forecast_min_demand_date_d2,
              forecast_max_demand_d2: fD2Max.forecast_max_demand_d2,
              forecast_max_demand_date_d2: fD2Max.forecast_max_demand_date_d2,
              forecast_avg_demand_d2: avg("forecast_avg_demand_d2"),
              forecast_mode_demand_d2: avg("forecast_mode_demand_d2"),
            };
          })(),
        };
      });

      setSummaryData(aggregated);
    } catch (error) {
      console.error("Error fetching Haryana summary data:", error);
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // ── Chart data derivation ────────────────────────────────────────────────
  useEffect(() => {
    const factor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    const processed = summaryData.map((item) => ({
      slot: item.slot,
      slot_number: item.slot,
      slot_with_time:
        item.slot && item.time_string
          ? `${item.slot} (${item.time_string})`
          : item.slot,
      // Actual Load
      min_demand: (item.min_demand ?? 0) * factor,
      max_demand: (item.max_demand ?? 0) * factor,
      avg_demand: (item.avg_demand ?? 0) * factor,
      mode_demand: (item.mode_demand ?? 0) * factor,
      min_demand_date: item.date_of_min,
      max_demand_date: item.date_of_max,

      // Forecast D+1
      forecast_min_demand_d1: (item.forecast_min_demand_d1 ?? 0) * factor,
      forecast_max_demand_d1: (item.forecast_max_demand_d1 ?? 0) * factor,
      forecast_avg_demand_d1: (item.forecast_avg_demand_d1 ?? 0) * factor,
      forecast_mode_demand_d1: (item.forecast_mode_demand_d1 ?? 0) * factor,
      forecast_min_demand_date_d1: item.forecast_min_demand_date_d1,
      forecast_max_demand_date_d1: item.forecast_max_demand_date_d1,

      // Forecast D+2
      forecast_min_demand_d2: (item.forecast_min_demand_d2 ?? 0) * factor,
      forecast_max_demand_d2: (item.forecast_max_demand_d2 ?? 0) * factor,
      forecast_avg_demand_d2: (item.forecast_avg_demand_d2 ?? 0) * factor,
      forecast_mode_demand_d2: (item.forecast_mode_demand_d2 ?? 0) * factor,
      forecast_min_demand_date_d2: item.forecast_min_demand_date_d2,
      forecast_max_demand_date_d2: item.forecast_max_demand_date_d2,
    }));

    processed.sort((a, b) => a.slot - b.slot);

    const filtered =
      selectedBlock.length > 0
        ? processed.filter((item) =>
            selectedBlock.map((b) => b.value).includes(item.slot),
          )
        : processed;

    setChartData(filtered);
    setTableData(filtered);
  }, [summaryData, selectedUnit, selectedBlock]);

  // ── Legends — filtered by active Type selection ───────────────────────────
  const activeSuffixKeys = useMemo(() => {
    return new Set(selectedSuffix.map((s) => s.suffix));
  }, [selectedSuffix]);

  const legends = useMemo(() => {
    const all = [
      // Actual Load
      {
        dataKey: "min_demand",
        name: "Min Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        dateKey: "min_demand_date",
        suffixType: "demand",
      },
      {
        dataKey: "max_demand",
        name: "Max Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        dateKey: "max_demand_date",
        suffixType: "demand",
      },
      {
        dataKey: "avg_demand",
        name: "Average Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        suffixType: "demand",
      },
      {
        dataKey: "mode_demand",
        name: "Mode Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        suffixType: "demand",
      },
      // Forecast D+1 Load
      {
        dataKey: "forecast_min_demand_d1",
        name: "Min (Forecast D+1)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d1" : undefined,
        dateKey: "forecast_min_demand_date_d1",
        suffixType: "demand_forecast_d1",
      },
      {
        dataKey: "forecast_max_demand_d1",
        name: "Max (Forecast D+1)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d1" : undefined,
        dateKey: "forecast_max_demand_date_d1",
        suffixType: "demand_forecast_d1",
      },
      {
        dataKey: "forecast_avg_demand_d1",
        name: "Average (Forecast D+1)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d1" : undefined,
        suffixType: "demand_forecast_d1",
      },
      {
        dataKey: "forecast_mode_demand_d1",
        name: "Mode (Forecast D+1)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d1" : undefined,
        suffixType: "demand_forecast_d1",
      },
      // Forecast D+2 Load
      {
        dataKey: "forecast_min_demand_d2",
        name: "Min (Forecast D+2)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d2" : undefined,
        dateKey: "forecast_min_demand_date_d2",
        suffixType: "demand_forecast_d2",
      },
      {
        dataKey: "forecast_max_demand_d2",
        name: "Max (Forecast D+2)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d2" : undefined,
        dateKey: "forecast_max_demand_date_d2",
        suffixType: "demand_forecast_d2",
      },
      {
        dataKey: "forecast_avg_demand_d2",
        name: "Average (Forecast D+2)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d2" : undefined,
        suffixType: "demand_forecast_d2",
      },
      {
        dataKey: "forecast_mode_demand_d2",
        name: "Mode (Forecast D+2)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d2" : undefined,
        suffixType: "demand_forecast_d2",
      },
    ];
    return all.filter((l) => activeSuffixKeys.has(l.suffixType));
  }, [selectedChartType, activeSuffixKeys]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends as any);

  // ── Tooltip ──────────────────────────────────────────────────────────────
  const customTooltipFormatter = useCallback(
    (params: any) => {
      const param = Array.isArray(params) ? params[0] : params;
      const items = Array.isArray(params) ? params : [params];
      const dataPoint = chartData[param.dataIndex];
      const title = dataPoint?.slot_with_time
        ? String(dataPoint.slot_with_time)
        : getTimeRange(parseInt(param.name)) + ` (${param.name})`;

      const tooltipItems = [...items]
        .sort((a, b) => (a.seriesName ?? "").localeCompare(b.seriesName ?? ""))
        .map((item) => {
          const value = parseFloat(item.value)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

          let displayName = item.seriesName;
          if (item.seriesName === "Min Demand" && dataPoint?.min_demand_date)
            displayName = `Min Demand (${dataPoint.min_demand_date})`;
          else if (
            item.seriesName === "Max Demand" &&
            dataPoint?.max_demand_date
          )
            displayName = `Max Demand (${dataPoint.max_demand_date})`;
          else if (
            item.seriesName === "Min (Forecast D+1)" &&
            dataPoint?.forecast_min_demand_date_d1
          )
            displayName = `Min (Forecast D+1) (${dataPoint.forecast_min_demand_date_d1})`;
          else if (
            item.seriesName === "Max (Forecast D+1)" &&
            dataPoint?.forecast_max_demand_date_d1
          )
            displayName = `Max (Forecast D+1) (${dataPoint.forecast_max_demand_date_d1})`;
          else if (
            item.seriesName === "Min (Forecast D+2)" &&
            dataPoint?.forecast_min_demand_date_d2
          )
            displayName = `Min (Forecast D+2) (${dataPoint.forecast_min_demand_date_d2})`;
          else if (
            item.seriesName === "Max (Forecast D+2)" &&
            dataPoint?.forecast_max_demand_date_d2
          )
            displayName = `Max (Forecast D+2) (${dataPoint.forecast_max_demand_date_d2})`;

          return `
          <div class="tooltip-item">
            <div class="color-indicator" style="background-color:${item.color}"></div>
            <div class="series-name">${displayName}</div>
            <div class="value">${value} ${selectedUnit}</div>
          </div>`;
        });

      return `
      <div class="enhanced-tooltip">
        <div class="tooltip-header">
          <div class="tooltip-title">${title}</div>
        </div>
        <div class="tooltip-content">${tooltipItems.join("")}</div>
      </div>
      <style>
        .enhanced-tooltip { font-family: 'Inter', system-ui, sans-serif; min-width: 200px; }
        .tooltip-header { border-bottom: 1px solid #eaeaea; padding-bottom: 8px; margin-bottom: 8px; }
        .tooltip-title { font-weight: 600; font-size: 13px; color: #333; }
        .tooltip-content { max-height: 300px; overflow-y: auto; }
        .tooltip-item { display: flex; align-items: center; gap: 8px; padding: 4px 0; border-bottom: 1px solid #f0f0f0; }
        .tooltip-item:last-child { border-bottom: none; }
        .color-indicator { width: 10px; height: 10px; flex-shrink: 0; border-radius: 50%; }
        .series-name { font-weight: 500; font-size: 12px; color: #333; flex: 1; }
        .value { font-weight: 600; font-size: 12px; color: #333; }
      </style>`;
    },
    [chartData, selectedUnit],
  );

  function getSelectedTypes() {
    return selectedSuffix.map((s) => s.displayName);
  }

  // ── Table props ───────────────────────────────────────────────────────────
  const tablePar = {
    legends: visibleLegends as any,
    data: chartData,
    tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    block_key: "slot_number",
    extraKeys: ["slot_number", "Date"],
    extraLabels: ["Block", "Date"],
    filename: AddDatesWithFileName({
      fileName: "Haryana Load Trend Summary",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => onUnitChange(item)}
          />
          <FilterMenu
            title="Type"
            items={chartSuffixes
              .filter((s) => s.suffix !== "deviation")
              .map((s) => s.displayName)}
            selectedItems={getSelectedTypes()}
            setSelectedItems={onTypeChange}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_, index) => setSelectedChartType(H_CHART_TYPES[index])}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">
                  Forecast Summary ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart")
                        downloadChartTableCSV(tablePar);
                      else if (dataTab.label === "Summary")
                        downloadSummaryCSV(tablePar);
                      else if (dataTab.label === "Table")
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedChartType.value}-${selectedUnit}`}
                  data={chartData}
                  showBrush={true}
                  isTimeSlot={false}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends as any}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey="slot"
                  secondXDataKey={undefined}
                  customTooltip={customTooltipFormatter}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip note={<p>HPPC</p>} text="Source">
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
